import { useEffect, useMemo, useState } from "react";
import { Localized, useLocalization } from "@fluent/react";
import {
    Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    List, ListItemText, Skeleton, Typography
} from "@mui/material";
import { useAppDispatch } from "../../../../app/hooks";
import { APIError, unknownError } from "../../../../app/appTypes";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { CancelButton } from "../../../../app/components/CancelButton";
import { LpField } from "../../../../app/components/LpField";
import { SaveButton } from "../../../../app/components/SaveButton";
import {
    CompanyDepartmentDetails, CompanyDepartmentMember,
    loadCompanyDepartments, setCompanyDepartmentMembers
} from "../companyDepartmentsSlice";
import { DepartmentCodeBadge } from "../components/DepartmentCodeBadge";

interface CompanyDepartmentNotificationsDialogProps {
    department: CompanyDepartmentDetails;
    onCancel: () => void;
    onSuccessfullySaved: (departments: CompanyDepartmentDetails[]) => void;
}

const userKey = (name: string) => name.toLowerCase();

export const CompanyDepartmentNotificationsDialog = ({
    department, onCancel, onSuccessfullySaved
}: CompanyDepartmentNotificationsDialogProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [members, setMembers] = useState<CompanyDepartmentMember[]>([]);
    const [originalMembers, setOriginalMembers] = useState<CompanyDepartmentMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stale, setStale] = useState(false);
    const [serverError, setServerError] = useState<APIError | null>(null);

    useEffect(() => {
        let cancelled = false;
        dispatch(loadCompanyDepartments()).then((result) => {
            if (cancelled) return;
            if (loadCompanyDepartments.fulfilled.match(result)) {
                const current = result.payload.find((item) => item.id === department.id);
                if (current) {
                    setMembers(current.members ?? []);
                    setOriginalMembers(current.members ?? []);
                } else {
                    setServerError({ kind: "http", status: 404 });
                }
            } else {
                setServerError(result.payload ?? unknownError());
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [department.id, dispatch]);

    const sortedMembers = useMemo(
        () => [...members].sort((a, b) =>
            (a.email || a.userName).localeCompare(b.email || b.userName)),
        [members]
    );
    const hasChanges = members.some((member) =>
        member.isNotificationReceiver !== originalMembers.find(
            (initial) => userKey(initial.userName) === userKey(member.userName)
        )?.isNotificationReceiver
    );

    const handleSave = async () => {
        setSaving(true);
        setServerError(null);
        try {
            const latestResult = await dispatch(loadCompanyDepartments());
            if (!loadCompanyDepartments.fulfilled.match(latestResult)) {
                setServerError(latestResult.payload ?? unknownError());
                return;
            }
            const latest = latestResult.payload.find((item) => item.id === department.id);
            if (!latest) {
                setServerError({ kind: "http", status: 404 });
                return;
            }
            const latestMembers = latest.members ?? [];
            const originalByName = new Map(originalMembers.map((member) => [userKey(member.userName), member]));
            if (latestMembers.length !== originalMembers.length ||
                latestMembers.some((member) => {
                    const initial = originalByName.get(userKey(member.userName));
                    return !initial || initial.isNotificationReceiver !== member.isNotificationReceiver;
                })) {
                setStale(true);
                return;
            }

            const saveResult = await dispatch(setCompanyDepartmentMembers({
                id: department.id,
                dto: {
                    members: members.map((member) => ({
                        userName: member.userName,
                        isNotificationReceiver: member.isNotificationReceiver
                    }))
                }
            }));
            if (!setCompanyDepartmentMembers.fulfilled.match(saveResult)) {
                setServerError(saveResult.payload ?? unknownError());
                return;
            }

            const refreshedResult = await dispatch(loadCompanyDepartments());
            if (loadCompanyDepartments.fulfilled.match(refreshedResult)) {
                onSuccessfullySaved(refreshedResult.payload);
            } else {
                setServerError(refreshedResult.payload ?? unknownError());
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
            <DialogTitle sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: "grey.200", color: "text.primary", fontWeight: "bold"
            }}>
                <DepartmentCodeBadge code={department.code} color={department.color} />
                <Localized id="company-department-notifications-title">
                    <span>_Department notifications</span>
                </Localized>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <Localized id="company-department-notifications-hint">
                        <span>_Choose which members receive department notifications.</span>
                    </Localized>
                </Typography>
                <ServerErrorMsg err={serverError} />
                {stale && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        <Localized id="company-department-notifications-stale">
                            <span>_Department members or notification settings changed. Close and reopen this dialog.</span>
                        </Localized>
                    </Alert>
                )}
                {loading ? <Skeleton height={120} /> : (
                    <List dense sx={{ maxHeight: "min(60vh, 540px)", overflowY: "auto" }}>
                        {sortedMembers.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                <Localized id="no-users-selected"><span>_No users selected</span></Localized>
                            </Typography>
                        )}
                        {sortedMembers.map((member, index) => {
                            const name = `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() || member.userName;
                            return (
                                <Box key={member.userName} sx={{
                                    px: 1, py: 1, display: "flex", alignItems: "center", gap: 1.5,
                                    borderBottom: 1, borderColor: "divider"
                                }}>
                                    <LpField value={index + 1} />
                                    <ListItemText primary={name} secondary={member.email || member.userName} />
                                    <Checkbox
                                        checked={member.isNotificationReceiver}
                                        disabled={saving || stale}
                                        inputProps={{
                                            "aria-label": l10n.getString("company-department-notifications-receiver",
                                                { name }, `_Notifications for ${name}`)
                                        }}
                                        onChange={(event) => setMembers((current) => current.map((item) =>
                                            item.userName === member.userName
                                                ? { ...item, isNotificationReceiver: event.target.checked }
                                                : item
                                        ))}
                                    />
                                </Box>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} disabled={loading || stale || !hasChanges} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
